import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card as MuiCard, CardContent as MuiCardContent } from '@mui/material';
import { Dialog, DialogContent, SwipeableDrawer } from '@mui/material';
import { AppBar, Toolbar, IconButton, Typography, Box } from '@mui/material';
import { ChevronLeft, Sparkles, MessageCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../theme.tsx';
import cardItems from '@/constants/cards.json';
import Spread from './Spread';
import useCards from '@/hooks/useCards';
import CardDialog from '@/components/reading/CardDialog';
import ReadingLike from '@/components/reading/ReadingLike';
import { useAuth } from '@/context/AuthContext';
import Toast from '@/components/utils/Toast';
import { track } from '@/third-party/MixPanel';
import { useAudioPlayerContext } from '@/context/AudioPlayerContext';
import { AudioPlayer } from '@/components/ui/audio-player';
import AudioReadingButton from '@/components/ui/AudioReadingButton';
import ShareReading from './ShareReading';
import DirectAnswerCard from '@/components/reading/DirectAnswerCard';
import CardInteractionsSection from '@/components/reading/CardInteractionsSection';
import PracticalAdviceSection from '@/components/reading/PracticalAdviceSection';
import ConclusionSection from '@/components/reading/ConclusionSection';

interface Card {
  id: number;
  card_name: string;
  primary_meaning: string;
  interpretation: string;
  reversed: boolean;
}

interface CardInteraction {
  cards_involved: (string | { id: number; name?: string; card_name?: string })[];
  interaction_description: string;
}

interface ReadingMetadata {
  type: string;
  setup: string;
  question: string;
  cards?: any[]; // Add this to match the API response
}

interface DetailedReading {
  cards?: Card[];
  card_interactions?: CardInteraction[];
  final_conclusion: string;
  direct_answer?: string;
}

interface Reading {
  cards?: Array<{ id: number; reversed: boolean }>;
  interpretation: {
    metadata: ReadingMetadata;
    detailed_reading: DetailedReading;
    practical_advice: string[];
  };
}

interface SelectedCardType {
  id: number;
  card_name: string;
  primary_meaning: string;
  interpretation: string;
  reversed: boolean;
}


const getCardById = (cardId: number): any => {
  return cardItems.cards.find((card: { id: number }) => card.id === cardId) || null;
};

// Common card component for consistent styling
const CommonCard = ({ children, sx = {} }: { children: React.ReactNode; sx?: any }) => (
  <MuiCard sx={{
    bgcolor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur-sm',
    border: '0.5px solid rgba(201, 173, 167, 0.4)',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 200ms',
    marginBottom: 8,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    },
    ...sx
  }}>
    {children}
  </MuiCard>
);

export default function Reading() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [cards, setCards] = useState<any[]>([]);
  const { id } = useParams();
  const { reading, getReadingById } = useCards();
  const metadata = reading?.interpretation?.metadata;
  const question = reading?.question;
  let detailed_reading = reading?.interpretation?.detailed_reading as DetailedReading;
  let cardsDescription = reading?.interpretation?.metadata?.cards;
  const [cardDescriptions, setCardDescriptions] = useState<{ [key: string]: any }>({});
  const [selectedCard, setSelectedCard] = useState<SelectedCardType | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Evaluation state
  const [evaluation, setEvaluation] = useState<boolean | null>(null);
  const [isEvaluationLoading, setIsEvaluationLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

  const { user } = useAuth();
  const { audios, isVisible, closePlayer, openSettings } = useAudioPlayerContext();

  useEffect(() => {
    const fetchReading = async () => {
      if (!hasLoaded && id) {
        try {
          console.log('Fetching reading for ID:', id);
          await getReadingById(id);

        } catch (error) {
          console.error('Error fetching reading:', error);
        } finally {
          setLoading(false);
          setHasLoaded(true);
          track('spread_reading_finished');
        }
      }
    };

    fetchReading();
  }, [id, hasLoaded]); // Removed getReadingById from dependencies to prevent unnecessary re-calls

  // Fetch evaluation status when reading is loaded
  useEffect(() => {
    const fetchEvaluationStatus = async () => {
      if (!id || !user || !hasLoaded) return;

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/tarot/reading/${id}/like-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setEvaluation(data.helpful);
        }
      } catch (error) {
        console.error('Error fetching evaluation status:', error);
      }
    };

    fetchEvaluationStatus();
  }, [id, user, hasLoaded]);

  useEffect(() => {
    if (detailed_reading?.cards) {
      const newCardsMap: { [key: string]: any } = {};
      detailed_reading.cards.forEach((card: any) => {
        newCardsMap[card.card_name] = card;
      });
      setCardDescriptions(newCardsMap);
    }
  }, [detailed_reading]);


  useEffect(() => {
    if (reading && reading.cards) {
      console.log('Reading.tsx - reading.cards:', JSON.stringify(reading.cards, null, 2));

      let visibleCards = (reading.interpretation?.detailed_reading?.cards || []).map((card: any, index: number) => {
        const reversed = reading.cards?.[index]?.reversed;
        console.log(`Reading.tsx - Card ${index}: ${card.card_name}, reversed: ${reversed}`);

        return {
          ...card,
          visible: true,
          reversed: reversed,
          description: {
            primary_meaning: card.primary_meaning,
            interpretation: card.interpretation,
            card_name: card.card_name
          }
        }
      })

      console.log('Reading.tsx - visibleCards with reversed info:', JSON.stringify(visibleCards.map(c => ({ id: c.id, card_name: c.card_name, reversed: c.reversed })), null, 2));
      setCards(visibleCards || []);
    }
  }, [reading, cardDescriptions]);

  console.log(reading)

  const handleEvaluationClick = async (isHelpful: boolean) => {
    if (!id || !user || isEvaluationLoading) return;
    track('spread_reading_evaluated', { isHelpful });
    setIsEvaluationLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/tarot/reading/${id}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ helpful: isHelpful })
      });

      if (response.ok) {
        const data = await response.json();
        setEvaluation(data.helpful);

        setToastMessage(isHelpful ? t('reading.helpfulSuccess') : t('reading.notHelpfulSuccess'));
        setToastSeverity('success');
        setToastOpen(true);
      } else {
        throw new Error('Failed to evaluate reading');
      }
    } catch (error) {
      console.error('Error evaluating reading:', error);
      setToastMessage(t('reading.evaluationError'));
      setToastSeverity('error');
      setToastOpen(true);
    } finally {
      setIsEvaluationLoading(false);
    }
  };



  const handleCardClick = (card: any) => {
    setSelectedCard({
      id: card.id,
      card_name: card.card_name || '',
      primary_meaning: card.primary_meaning || '',
      interpretation: card.interpretation || '',
      reversed: card.reversed || card.flipped || false
    });
  }

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            p: 3,
            paddingTop: 8,
            mx: 'auto',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            fontFamily: 'Plus Jakarta Sans'
          }}
        >
          <MuiCard sx={{
            bgcolor: 'transparent',
            boxShadow: 'none',
            border: 'none',
            textAlign: 'center',
            maxWidth: 400,
            width: '100%'
          }}>
            <MuiCardContent>
              <Box sx={{ mb: 3 }}>
                <Sparkles
                  className="animate-pulse mx-auto mb-3"
                  style={{
                    width: '48px',
                    height: '48px',
                    color: '#2B2E4A',
                    animation: 'pulse 2s infinite'
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Playfair Display',
                  color: '#2B2E4A',
                  mb: 2
                }}
              >
                {t('readingPage.loadingReading')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#2B2E4A',
                  opacity: 0.7,
                  fontFamily: 'Plus Jakarta Sans'
                }}
              >
                {t('readingPage.preparingYourReading')}
              </Typography>
            </MuiCardContent>
          </MuiCard>
        </Box>
      </ThemeProvider>
    );
  }

  if (!reading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
          <MuiCard sx={{ bgcolor: '#ECEAE5', boxShadow: 3 }}>
            <MuiCardContent>
              <Typography variant="h6" align="center">{t('readingPage.noReadingsFound')}</Typography>
              <Typography>{t('readingPage.tryNewReading')}</Typography>
            </MuiCardContent>
          </MuiCard>
        </Box>
      </ThemeProvider>
    );
  }

  if (cardsDescription) {
    cardsDescription = cardsDescription.map((card: any) => {

      if (card?.title) {
        return {
          ...card,
          visible: true,
          reversed: card.reversed,
          description: cardDescriptions[card.title]
        }
      }
      return card;
    });
  }



  return (
    <ThemeProvider theme={theme}>
      <Box
        component="main"
        sx={{
          p: 3,
          paddingTop: 5,
          mx: 'auto',

          minHeight: '100vh',
        }}
      >
        {/* Question Section */}
        <section aria-labelledby="question-heading">
          <h3
            id="question-heading"
            className="text-lg font-['Playfair_Display'] text-[#2B2E4A] font-semibold mb-4 mt-0 m-0"
          >
            {t('readingPage.yourQuestion')}
          </h3>
          <CommonCard
            sx={{
              mb: 2,
              mt: 1.2,
              maxWidth: 480,
            }}>
            <MuiCardContent>
              <div>
                {metadata?.question && metadata.question.length > 250 ? (
                  <>
                    {metadata.question && (
                      <p className="text-base leading-[1.7] text-[#2B2E4A] font-['Plus_Jakarta_Sans'] line-clamp-5 pb-0">
                        {metadata.question}
                      </p>
                    )}
                    <Button
                      onClick={() => setOpenModal(true)}
                      variant="ghost"
                      className="mt-2 text-[#2B2E4A] hover:bg-white/50"
                    >
                      {t('readingPage.readMore')}
                    </Button>
                    <Dialog
                      sx={{
                        '& .MuiDialog-paper': {
                          backgroundColor: '#ECEAE5',
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderRadius: '0.5rem',
                        }
                      }}
                      fullScreen
                      open={openModal}
                      onClose={() => setOpenModal(false)}
                      slotProps={{
                        backdrop: {
                          timeout: 100,
                        },
                      }}
                    >
                      <AppBar sx={{ position: 'relative', bgcolor: 'background.dark', boxShadow: 'none' }}>
                        <Toolbar>
                          <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => setOpenModal(false)}
                            aria-label="close"
                          >
                            <ChevronLeft />
                          </IconButton>
                          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
                            {t('readingPage.yourQuestion')}
                          </Typography>
                        </Toolbar>
                      </AppBar>
                      <DialogContent>
                        <Typography sx={{
                          color: 'text.primary',
                          fontStyle: 'italic',
                          textAlign: 'center',
                          whiteSpace: 'pre-line',
                          lineHeight: 1.6,
                          fontSize: '1.1rem',
                        }}>{question}</Typography>
                      </DialogContent>
                    </Dialog>
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.primary',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      whiteSpace: 'pre-line',
                      lineHeight: 1.6,
                      fontSize: '1.1rem',
                    }}
                  >
                    {question}
                  </Typography>
                )}
              </div>
            </MuiCardContent>
          </CommonCard>

          {/* Direct Answer Section */}
          <DirectAnswerCard
            directAnswer={(reading.interpretation?.detailed_reading as DetailedReading)?.direct_answer}
            finalConclusion={reading.interpretation?.detailed_reading?.final_conclusion}
            practicalAdvice={reading.interpretation?.practical_advice}
            onShare={() => setShareModalOpen(true)}
            showShareButton={true}
          />
        </section>

        {/* Spread Section */}
        <section aria-labelledby="spread-heading">
          <h2
            id="spread-heading"
            className="text-lg font-['Playfair_Display'] text-[#2B2E4A] font-semibold mb-0 mt-0 m-0"
          >
            {t('readingPage.theSpread')}
          </h2>
          <div className="w-full pb-12" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Spread
              type={metadata}
              cards={cards}

              setDrawnCards={() => { }}

            />
          </div>
        </section>

        {/* Card Interactions Section */}
        <CardInteractionsSection
          cardInteractions={detailed_reading?.card_interactions}
          cards={cards}
          onCardClick={handleCardClick}
          cardDescriptions={cardDescriptions}
        />

        {/* Practical Advice Section */}
        <PracticalAdviceSection practicalAdvice={reading.interpretation.practical_advice} />

        {/* Conclusion Section */}
        <ConclusionSection conclusion={detailed_reading?.final_conclusion} />



        {/* Like Component at the bottom */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 4,
          mb: 0,
          p: 2
        }}>
          {id && (
            <ReadingLike
              readingId={id}
              className=""
              evaluation={evaluation}
              isLoading={isEvaluationLoading}
              onEvaluationClick={handleEvaluationClick}
            />
          )}
        </Box>

        {/* Audio Player Component */}
        {isVisible && (
          <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(201, 173, 167, 0.3)',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <AudioPlayer
              audios={audios}
              isVisible={isVisible}
              onClose={closePlayer}
              onSettings={openSettings}
            />
          </Box>
        )}

        <Button
          className="fixed bottom-20 right-6 rounded-full w-16 h-16 bg-[#2B2E4A] hover:bg-[#3D4066] shadow-lg  md:block transition-colors hidden"
          onClick={() => navigate(`/chat/${id}`)}
        >
          <MessageCircle className="h-8 w-8 text-white" />
        </Button>

        {/* Floating Audio Button */}
        <AudioReadingButton readingId={id} sx={{ display: 'none' }} />
      </Box>

      <SwipeableDrawer
        anchor="bottom"
        open={!!selectedCard}
        onClose={() => setSelectedCard(null)}
        onOpen={() => { }}
        disableSwipeToOpen={true}
        slotProps={{
          paper: {
            sx: {
              height: '700px',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              padding: '20px'
            }
          }
        }}
      >
        {selectedCard && (
          <Box sx={{ p: 0 }}>
            <CardDialog
              open={!!selectedCard}
              onClose={() => setSelectedCard(null)}
              card={selectedCard}
            />
          </Box>
        )}
      </SwipeableDrawer>

      <Toast
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastOpen(false)}
      />

      {/* Share Reading Modal */}
      <ShareReading
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        cards={cards}
        type={metadata}


        question={question || ''}
        snSentence={
          !!(reading.interpretation?.detailed_reading as any)?.social_network_sentence
            ? (reading.interpretation?.detailed_reading as any)?.social_network_sentence
            : reading.interpretation?.detailed_reading?.final_conclusion.split(".")[0]
        }
      />
    </ThemeProvider >
  );
}
